// Fleet management (GDD 08 screen 3, slice subset): incoming alerts, fleets in flight with recall,
// reinforcements abroad with return, garrisons, and links to Send fleet (`./dispatch.tsx`).
import type { FleetDetection, FullSystem, OwnFleet } from '@stars-and-ships/api-types';
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { Rocket, ScrollText, Siren, Swords } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router';
import { endpoints } from '../api/endpoints';
import { queryKeys, useFleetOrders, useFleets, useMap, useVisibleFleets } from '../api/queries';
import { ScheduledOrders } from '../components/fleet/ScheduledOrders';
import type { GameContext } from '../components/GameLayout';
import { Button, ErrorNotice, Page, Panel, Spinner } from '../components/ui';
import { reinforceLink } from '../fleetTiming';
import {
  FLEET_STATUS_LABELS,
  formatDuration,
  formatNumber,
  formatShips,
  MISSION_LABELS,
  RESOURCE_KEYS,
  RESOURCE_LABELS,
} from '../labels';
import { useNow } from '../state/live';

export function useSystemNames(galaxyId: string): (id: string | null | undefined) => string {
  const map = useMap(galaxyId);
  const names = useMemo(
    () => new Map(map.data?.systems.map((s) => [s.id, s.name]) ?? []),
    [map.data]
  );
  return (id) => (id ? (names.get(id) ?? 'Unknown system') : '—');
}

export function invalidateMilitary(queryClient: QueryClient, galaxyId: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.fleets(galaxyId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.systems(galaxyId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.battles(galaxyId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.galaxyMe(galaxyId) });
}

const countdown = (iso: string | null, now: number) =>
  iso ? (Date.parse(iso) > now ? formatDuration(Date.parse(iso) - now) : 'arriving…') : '—';

function IncomingAlert({
  detection,
  systemName,
  now,
}: {
  detection: FleetDetection;
  systemName: string;
  now: number;
}) {
  return (
    <li
      data-incoming={detection.fleetId}
      className="flex items-start gap-3 hud-cut-sm border border-danger/50 bg-danger/10 p-3 text-sm"
    >
      <Siren className="mt-0.5 size-5 shrink-0 text-danger" aria-hidden />
      <div className="min-w-0">
        <p className="text-danger">
          Hostile fleet of {detection.attacker.displayName}
          {detection.attacker.allianceTag ? ` [${detection.attacker.allianceTag}]` : ''} heading to{' '}
          {systemName}
        </p>
        <p className="font-mono text-xs tabular-nums text-danger">
          arrives in {countdown(detection.arrivesAt, now)}
          {detection.ships ? ` · ${formatShips(detection.ships)}` : ''}
          {detection.sizeClass ? ` · ${detection.sizeClass} fleet` : ''}
        </p>
      </div>
    </li>
  );
}

/** Alerts and actions above the system screen: incoming fleets, the running battle, send fleet. */
export function SystemMilitaryBar({
  galaxyId,
  system,
  now,
  allied = false,
  sendLink = true,
}: {
  galaxyId: string;
  system: FullSystem;
  now: number;
  /** An ally's system: offer reinforcements instead of sending from it. */
  allied?: boolean;
  /** Off when the screen shows its own send-fleet link (the own-system header). */
  sendLink?: boolean;
}) {
  return (
    <div className="space-y-2">
      {system.activeBattleId ? (
        <Link
          to={`/g/${galaxyId}/battles/${system.activeBattleId}`}
          data-active-battle
          className="flex items-center gap-2 hud-cut-sm border border-accent/50 bg-accent/10 p-3 text-sm text-accent"
        >
          <Swords className="size-5 text-accent" aria-hidden />A battle is being fought here — view
          battle
        </Link>
      ) : null}
      {system.incomingDetections.length > 0 ? (
        <ul className="space-y-2">
          {system.incomingDetections.map((d) => (
            <IncomingAlert key={d.fleetId} detection={d} systemName={system.name} now={now} />
          ))}
        </ul>
      ) : null}
      {sendLink ? (
        <Link
          to={
            allied
              ? `/g/${galaxyId}/fleet/send?target=${system.id}`
              : `/g/${galaxyId}/fleet/send?origin=${system.id}`
          }
          className="inline-flex min-h-11 items-center gap-2 hud-cut-sm border border-line-strong bg-panel-raised px-4 text-sm font-semibold text-text hover:border-accent"
        >
          <Rocket className="size-4" aria-hidden />{' '}
          {allied ? 'Send reinforcements' : 'Send fleet from here'}
        </Link>
      ) : null}
    </div>
  );
}

function FleetRow({
  galaxyId,
  fleet,
  name,
  now,
  owner,
  children,
}: {
  galaxyId: string;
  fleet: OwnFleet;
  /** An alliance member's fleet (shared intel): the member's name. */
  owner?: string;
  name: (id: string | null | undefined) => string;
  now: number;
  children?: React.ReactNode;
}) {
  const where =
    fleet.status === 'in_transit' || fleet.status === 'returning'
      ? `${name(fleet.originSystemId)} → ${name(fleet.targetSystemId)}`
      : name(fleet.locationSystemId);
  return (
    <li
      data-fleet={fleet.id}
      data-fleet-status={fleet.status}
      className="hud-cut-sm border border-line bg-void/60 p-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-text">
            {owner ? <span className="text-info">{owner} · </span> : null}
            {fleet.isGarrison
              ? 'Garrison'
              : fleet.mission
                ? MISSION_LABELS[fleet.mission]
                : 'Fleet'}{' '}
            · <span className="text-muted">{FLEET_STATUS_LABELS[fleet.status]}</span>
          </p>
          <p className="truncate text-xs text-muted">{where}</p>
          <p className="text-xs text-muted">{formatShips(fleet.ships)}</p>
          {fleet.cargo.metal + fleet.cargo.gas + fleet.cargo.supplies > 0 ? (
            <p className="text-xs text-accent">
              Cargo:{' '}
              {RESOURCE_KEYS.map(
                (k) => `${formatNumber(fleet.cargo[k])} ${RESOURCE_LABELS[k]}`
              ).join(' · ')}
            </p>
          ) : null}
          {fleet.arrivesAt && (fleet.status === 'in_transit' || fleet.status === 'returning') ? (
            <p className="font-mono text-xs tabular-nums text-info">
              arrives in {countdown(fleet.arrivesAt, now)}
            </p>
          ) : null}
          {fleet.status === 'in_transit' || fleet.status === 'returning' ? (
            <Link to={`/g/${galaxyId}?fleet=${fleet.id}`} className="text-xs text-info underline">
              Show on map
            </Link>
          ) : null}
          {fleet.battleId ? (
            <Link
              to={`/g/${galaxyId}/battles/${fleet.battleId}`}
              className="text-xs text-accent underline"
            >
              View battle
            </Link>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col gap-1">{children}</div>
      </div>
    </li>
  );
}

export function FleetScreen() {
  const { galaxyId, me } = useOutletContext<GameContext>();
  const fleets = useFleets(galaxyId);
  const visible = useVisibleFleets(galaxyId);
  const name = useSystemNames(galaxyId);
  const now = useNow();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const outcome = {
    onSuccess: () => {
      setError(null);
      invalidateMilitary(queryClient, galaxyId);
    },
    onError: (e: unknown) => setError(e),
  };
  const recall = useMutation({
    mutationFn: (id: string) => endpoints.recall(galaxyId, id),
    ...outcome,
  });
  const withdraw = useMutation({
    mutationFn: (id: string) => endpoints.returnFleet(galaxyId, id),
    ...outcome,
  });
  const share = useMutation({
    mutationFn: (f: OwnFleet) => endpoints.shareFleet(galaxyId, f.id, !f.shared),
    ...outcome,
  });
  const orders = useFleetOrders(galaxyId);
  const pending = recall.isPending || withdraw.isPending || share.isPending;

  const items = fleets.data?.items ?? [];
  const moving = items.filter((f) => f.status === 'in_transit' || f.status === 'returning');
  const fighting = items.filter((f) => f.status === 'in_battle' || f.status === 'waiting');
  const abroad = items.filter((f) => f.status === 'stationed' && !f.isGarrison);
  const garrisons = items.filter((f) => f.isGarrison && f.status === 'stationed');
  const incoming = (visible.data?.items ?? []).flatMap((v) =>
    v.relation === 'detected' ? [v.detection] : []
  );
  const allied = (visible.data?.items ?? []).flatMap((v) => (v.relation === 'alliance' ? [v] : []));

  return (
    <Page width="wide">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-display text-xl font-bold uppercase tracking-[0.08em] text-text">
          Fleets
        </h1>
        <div className="flex gap-2">
          <Link
            to={`/g/${galaxyId}/battles`}
            className="inline-flex min-h-11 items-center gap-2 hud-cut-sm border border-line-strong bg-panel-raised px-4 text-sm font-semibold text-text"
          >
            <ScrollText className="size-4" aria-hidden /> Battle reports
          </Link>
          <Link
            to={`/g/${galaxyId}/fleet/send`}
            className="inline-flex min-h-11 items-center gap-2 hud-cut-sm bg-accent px-4 text-sm font-semibold text-accent-ink"
          >
            <Rocket className="size-4" aria-hidden /> Send fleet
          </Link>
        </div>
      </div>
      {error ? <ErrorNotice error={error} /> : null}
      {fleets.isPending ? <Spinner /> : null}
      {fleets.error ? <ErrorNotice error={fleets.error} /> : null}

      {/* From lg: movements on the left, what is stationed on the right. */}
      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <div className="space-y-4">
          {incoming.length > 0 ? (
            <Panel title="Incoming">
              <ul className="space-y-2">
                {incoming.map((d) => (
                  <IncomingAlert
                    key={d.fleetId}
                    detection={d}
                    systemName={name(d.targetSystemId)}
                    now={now}
                  />
                ))}
              </ul>
            </Panel>
          ) : null}

          <Panel title="Scheduled">
            {(orders.data?.items ?? []).length === 0 ? (
              <p className="text-sm text-muted">No timed fleets.</p>
            ) : (
              <ScheduledOrders
                galaxyId={galaxyId}
                orders={orders.data?.items ?? []}
                name={name}
                now={now}
                onChanged={() => invalidateMilitary(queryClient, galaxyId)}
                canShare={Boolean(me?.alliance)}
                showReinforce
              />
            )}
          </Panel>

          <Panel title="In flight">
            {moving.length === 0 ? (
              <p className="text-sm text-muted">No fleets in flight.</p>
            ) : (
              <ul className="space-y-2">
                {moving.map((f) => (
                  <FleetRow key={f.id} galaxyId={galaxyId} fleet={f} name={name} now={now}>
                    {f.status === 'in_transit' ? (
                      <Button
                        variant="ghost"
                        disabled={pending}
                        onClick={() => recall.mutate(f.id)}
                      >
                        Recall
                      </Button>
                    ) : null}
                    {f.status === 'in_transit' &&
                    (f.mission === 'attack' || f.mission === 'raid') &&
                    f.targetSystemId &&
                    f.arrivesAt ? (
                      <>
                        {me?.alliance ? (
                          <Button
                            variant="ghost"
                            disabled={pending}
                            onClick={() => share.mutate(f)}
                          >
                            {f.shared ? 'Unshare' : 'Share'}
                          </Button>
                        ) : null}
                        <Link
                          className="text-xs text-info underline"
                          to={reinforceLink(galaxyId, {
                            targetSystemId: f.targetSystemId,
                            mission: f.mission,
                            arriveAt: f.arrivesAt,
                          })}
                        >
                          Reinforce {f.mission}
                        </Link>
                      </>
                    ) : null}
                  </FleetRow>
                ))}
              </ul>
            )}
          </Panel>

          {allied.length > 0 ? (
            <Panel title="Alliance fleets in flight">
              <ul className="space-y-2">
                {allied.map((v) => (
                  <FleetRow
                    key={v.fleet.id}
                    galaxyId={galaxyId}
                    fleet={v.fleet}
                    owner={v.owner.displayName}
                    name={name}
                    now={now}
                  />
                ))}
              </ul>
            </Panel>
          ) : null}

          {fighting.length > 0 ? (
            <Panel title="Battles">
              <ul className="space-y-2">
                {fighting.map((f) => (
                  <FleetRow key={f.id} galaxyId={galaxyId} fleet={f} name={name} now={now} />
                ))}
              </ul>
            </Panel>
          ) : null}
        </div>

        <div className="space-y-4">
          {abroad.length > 0 ? (
            <Panel title="Reinforcing allies">
              <ul className="space-y-2">
                {abroad.map((f) => (
                  <FleetRow key={f.id} galaxyId={galaxyId} fleet={f} name={name} now={now}>
                    <Button
                      variant="ghost"
                      disabled={pending}
                      onClick={() => withdraw.mutate(f.id)}
                    >
                      Return
                    </Button>
                  </FleetRow>
                ))}
              </ul>
            </Panel>
          ) : null}

          <Panel title="Garrisons">
            {garrisons.length === 0 ? (
              <p className="text-sm text-muted">No ships stationed.</p>
            ) : (
              <ul className="space-y-2">
                {garrisons.map((f) => (
                  <FleetRow key={f.id} galaxyId={galaxyId} fleet={f} name={name} now={now}>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        navigate(`/g/${galaxyId}/fleet/send?origin=${f.locationSystemId}`)
                      }
                    >
                      Send
                    </Button>
                  </FleetRow>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </Page>
  );
}
